/**
 * Loans — portfolio analytics across all banks (PHASE2_SPEC §4.2).
 *
 * Layout:
 *   Filter bar (date + bank multi-select)
 *   KPI tiles (loan ₹Cr · accounts · NPA ₹Cr · NPA %)
 *   Bar chart — NPA category distribution
 *   Top 20 loan products (sortable, clickable → /products/:productCode?type=LOAN)
 *   Per-bank table (sortable, clickable → /banks/:orgId)
 *   Per-branch table — only when ≥ 1 bank is filtered (clickable → branch detail)
 *
 * Filter state lives in URL search params so back/forward and bookmarking
 * restore the view exactly.
 */

import React, { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import AnalyticsFilterBar from '../../components/AnalyticsFilterBar';
import BankBadge from '../../components/BankBadge';
import BarChart from '../../components/BarChart';
import Card from '../../components/Card';
import EmptyState from '../../components/EmptyState';
import ErrorBanner from '../../components/ErrorBanner';
import KpiTile from '../../components/KpiTile';
import LoadingSkeleton from '../../components/LoadingSkeleton';
import SortableTh from '../../components/SortableTh';
import StatusPill from '../../components/StatusPill';
import { formatDate } from '../../helpers/dateFormat';
import { npaFlag } from '../../helpers/flag';
import { formatAbsCrore, formatCount, formatCrore, formatPct } from '../../helpers/format';
import { NPA_COLORS } from '../../helpers/palette';
import { useSortable } from '../../hooks/useSortable';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchLoans, selectLoans } from '../../redux/slices/dashboard';
import { fetchBanks, selectBanks } from '../../redux/slices/banks';
import type { LoanByBankRow, LoanByBranchRow, LoanProductRow } from '../../type/api';

const NPA_CATEGORY_COLOR: Record<string, string> = {
	Standard: NPA_COLORS.standard,
	'Sub-Standard': NPA_COLORS.subStandard,
	Doubtful: NPA_COLORS.doubtful,
	Loss: NPA_COLORS.loss,
};

function parseOrgIds(raw: string | null): number[] {
	if (!raw) return [];
	return raw
		.split(',')
		.map((s) => parseInt(s, 10))
		.filter((n) => Number.isFinite(n) && n > 0);
}

export default function LoansPage() {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const cell = useAppSelector(selectLoans);
	const banks = useAppSelector(selectBanks);

	const [params, setParams] = useSearchParams();
	const date = params.get('date');
	const orgIds = useMemo(() => parseOrgIds(params.get('orgIds')), [params]);

	// Fetch the bank list once for the multi-select.
	useEffect(() => {
		if (banks.length === 0) dispatch(fetchBanks());
	}, [banks.length, dispatch]);

	// Re-fetch whenever the filter changes (or on first mount).
	useEffect(() => {
		dispatch(fetchLoans({ date: date ?? undefined, orgIds: orgIds.length ? orgIds : undefined }));
		// orgIds is derived from params so only params is the real dep.
	}, [dispatch, date, params]); // eslint-disable-line react-hooks/exhaustive-deps

	const data = cell.data;
	const products: LoanProductRow[] = useMemo(
		() => (Array.isArray(data?.topProducts) ? data!.topProducts : []),
		[data],
	);
	const byBank: LoanByBankRow[] = useMemo(
		() => (Array.isArray(data?.byBank) ? data!.byBank : []),
		[data],
	);
	const byBranch: LoanByBranchRow[] = useMemo(
		() => (Array.isArray(data?.byBranch) ? data!.byBranch : []),
		[data],
	);

	const productSort = useSortable<LoanProductRow>(products, { field: 'loanCr', dir: 'desc' });
	const bankSort = useSortable<LoanByBankRow>(byBank, { field: 'loanCr', dir: 'desc' });
	const branchSort = useSortable<LoanByBranchRow>(byBranch, { field: 'loanCr', dir: 'desc' });

	const setDate = (next: string) => {
		const p = new URLSearchParams(params);
		if (next) p.set('date', next);
		else p.delete('date');
		setParams(p, { replace: true });
	};
	const setOrgIds = (ids: number[]) => {
		const p = new URLSearchParams(params);
		if (ids.length) p.set('orgIds', ids.join(','));
		else p.delete('orgIds');
		setParams(p, { replace: true });
	};

	const handleRetry = () =>
		dispatch(fetchLoans({ date: date ?? undefined, orgIds: orgIds.length ? orgIds : undefined }));

	const isLoading = cell.status === 'loading';

	const filterBar = (
		<AnalyticsFilterBar
			date={date ?? data?.asOf ?? null}
			availableDates={data?.snapshotsAvailable}
			onDateChange={setDate}
			banks={banks}
			selectedOrgIds={orgIds}
			onOrgIdsChange={setOrgIds}
			onRefresh={handleRetry}
			loading={isLoading}
		/>
	);

	if (cell.status === 'loading' && !data) {
		return (
			<div className="page-body">
				<div className="page-head">
					<div>
						<h1 className="page-title">Loans</h1>
					</div>
				</div>
				{filterBar}
				<Card>
					<LoadingSkeleton rows={6} columns={6} />
				</Card>
			</div>
		);
	}
	if (cell.status === 'error') {
		return (
			<div className="page-body">
				<div className="page-head">
					<div>
						<h1 className="page-title">Loans</h1>
					</div>
				</div>
				{filterBar}
				<ErrorBanner message={cell.error || 'Failed to load loans'} onRetry={handleRetry} />
			</div>
		);
	}
	if (!data) {
		return (
			<div className="page-body">
				<div className="page-head">
					<div>
						<h1 className="page-title">Loans</h1>
					</div>
				</div>
				{filterBar}
				<Card>
					<EmptyState
						title="No loan data"
						hint="Once the ETL completes a snapshot, this page will populate."
					/>
				</Card>
			</div>
		);
	}

	const summary = data.summary;
	const npaCategory = (Array.isArray(data.byNpaCategory) ? data.byNpaCategory : []).map((c) => ({
		name: c.category,
		value: Math.abs(c.loanCr),
		color: NPA_CATEGORY_COLOR[c.category] || 'var(--navy-500)',
	}));

	return (
		<div className="page-body">
			<div className="page-head">
				<div>
					<h1 className="page-title">Loans</h1>
					<div className="page-sub">
						Loan portfolio across all banks.
						{data.asOf && (
							<>
								{' · As of '}
								<span style={{ color: 'var(--text-dim)', fontWeight: 500 }}>
									{formatDate(data.asOf)}
								</span>
							</>
						)}
					</div>
				</div>
			</div>

			{filterBar}

			<div className="grid cols-4">
				<KpiTile label="Total Loan ₹Cr" value={formatCrore(summary.totalLoanCr, 2)} />
				<KpiTile label="Total Loan Accounts" value={formatCount(summary.totalLoanCount)} />
				<KpiTile
					label="NPA ₹Cr"
					value={formatCrore(summary.totalNpaCr, 2)}
					sub={`${formatCount(summary.totalNpaCount)} accounts`}
				/>
				<KpiTile
					label="NPA %"
					value={formatPct(summary.npaPct)}
					flag={npaFlag(summary.npaPct)}
					flagVariant="npa"
					primary
				/>
			</div>

			<Card title="NPA category distribution (₹ Cr)">
				<BarChart
					data={npaCategory}
					yAxisLabel="₹ Cr"
					valueFormatter={(v) => formatCrore(v, 1)}
				/>
			</Card>

			<Card
				title={`Top loan products (${products.length})`}
				tools={
					<span style={{ fontSize: 12, color: 'var(--text-faint)' }}>Click to drill in</span>
				}
			>
				<div style={{ overflowX: 'auto' }}>
					<table className="tbl" style={{ width: '100%' }}>
						<thead>
							<tr>
								<SortableTh field="productCode" sort={productSort}>
									Code
								</SortableTh>
								<SortableTh field="productName" sort={productSort}>
									Product
								</SortableTh>
								<SortableTh field="loanCr" sort={productSort} numeric>
									Loan ₹Cr
								</SortableTh>
								<SortableTh field="loanCount" sort={productSort} numeric>
									Accounts
								</SortableTh>
								<SortableTh field="npaPct" sort={productSort} numeric>
									NPA %
								</SortableTh>
							</tr>
						</thead>
						<tbody>
							{productSort.rows.map((p) => (
								<tr
									key={p.productCode}
									className="clickable"
									onClick={() =>
										navigate(`/products/${encodeURIComponent(p.productCode)}?type=LOAN`)
									}
									tabIndex={0}
								>
									<td style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
										{p.productCode}
									</td>
									<td>{p.productName}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										{formatAbsCrore(p.loanCr)}
									</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										{formatCount(p.loanCount)}
									</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										<StatusPill
											flag={npaFlag(p.npaPct)}
											variant="npa"
											compact
											label={formatPct(p.npaPct)}
										/>
									</td>
								</tr>
							))}
							{!productSort.rows.length && (
								<tr>
									<td colSpan={5}>
										<EmptyState title="No products" />
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</Card>

			<Card title={`Per-bank breakdown (${byBank.length})`}>
				<div style={{ overflowX: 'auto' }}>
					<table className="tbl" style={{ width: '100%' }}>
						<thead>
							<tr>
								<SortableTh field="orgId" sort={bankSort} numeric>
									ID
								</SortableTh>
								<SortableTh field="orgName" sort={bankSort}>
									Bank
								</SortableTh>
								<SortableTh field="loanCr" sort={bankSort} numeric>
									Loan ₹Cr
								</SortableTh>
								<SortableTh field="loanCount" sort={bankSort} numeric>
									Accounts
								</SortableTh>
								<SortableTh field="npaCr" sort={bankSort} numeric>
									NPA ₹Cr
								</SortableTh>
								<SortableTh field="npaPct" sort={bankSort} numeric>
									NPA %
								</SortableTh>
								<SortableTh field="npaFlag" sort={bankSort}>
									Status
								</SortableTh>
							</tr>
						</thead>
						<tbody>
							{bankSort.rows.map((b) => (
								<tr
									key={b.orgId}
									className="clickable"
									onClick={() => navigate(`/banks/${b.orgId}`)}
									tabIndex={0}
								>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										{b.orgId}
									</td>
									<td>
										<BankBadge orgId={b.orgId} orgName={b.orgName} />
									</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										{formatAbsCrore(b.loanCr)}
									</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										{formatCount(b.loanCount)}
									</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										{formatAbsCrore(b.npaCr)}
									</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										{formatPct(b.npaPct)}
									</td>
									<td>
										<StatusPill flag={b.npaFlag} variant="npa" />
									</td>
								</tr>
							))}
							{!bankSort.rows.length && (
								<tr>
									<td colSpan={7}>
										<EmptyState title="No banks" />
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</Card>

			{orgIds.length > 0 && (
				<Card
					title={`Per-branch breakdown (${byBranch.length}${
						byBranch.length >= 500 ? ', capped at 500' : ''
					})`}
					tools={
						<span style={{ fontSize: 12, color: 'var(--text-faint)' }}>
							Drill into a branch
						</span>
					}
				>
					<div style={{ overflowX: 'auto' }}>
						<table className="tbl" style={{ width: '100%' }}>
							<thead>
								<tr>
									<SortableTh field="orgName" sort={branchSort}>
										Bank
									</SortableTh>
									<SortableTh field="branchIdLocal" sort={branchSort}>
										Branch ID
									</SortableTh>
									<SortableTh field="branchName" sort={branchSort}>
										Branch
									</SortableTh>
									<SortableTh field="loanCr" sort={branchSort} numeric>
										Loan ₹Cr
									</SortableTh>
									<SortableTh field="loanCount" sort={branchSort} numeric>
										Accounts
									</SortableTh>
									<SortableTh field="npaCr" sort={branchSort} numeric>
										NPA ₹Cr
									</SortableTh>
									<SortableTh field="npaPct" sort={branchSort} numeric>
										NPA %
									</SortableTh>
									<SortableTh field="npaFlag" sort={branchSort}>
										Status
									</SortableTh>
								</tr>
							</thead>
							<tbody>
								{branchSort.rows.map((b) => (
									<tr
										key={`${b.orgId}-${b.branchIdLocal}`}
										className="clickable"
										onClick={() =>
											navigate(
												`/banks/${b.orgId}/branches/${encodeURIComponent(b.branchIdLocal)}`,
											)
										}
										tabIndex={0}
									>
										<td>
											<BankBadge orgId={b.orgId} orgName={b.orgName} />
										</td>
										<td style={{ fontVariantNumeric: 'tabular-nums' }}>
											{b.branchIdLocal}
										</td>
										<td>{b.branchName || '—'}</td>
										<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
											{formatAbsCrore(b.loanCr)}
										</td>
										<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
											{formatCount(b.loanCount)}
										</td>
										<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
											{formatAbsCrore(b.npaCr)}
										</td>
										<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
											{formatPct(b.npaPct)}
										</td>
										<td>
											<StatusPill flag={b.npaFlag} variant="npa" />
										</td>
									</tr>
								))}
								{!branchSort.rows.length && (
									<tr>
										<td colSpan={8}>
											<EmptyState
												title="No branch data"
												hint="The selected banks have no branches in tblBranchSummary for this snapshot."
											/>
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
				</Card>
			)}
		</div>
	);
}
